import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { ncFileStatisticsTimes } from '@reducers/statistics';
import { selectSingleBatchData, setBatch } from '@reducers/batch';
import { EventsPerspective } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { ClampingDetails, NCFileStatisticsTimesType } from '@v2/context/Times/page/interfaces';
import { mapClampingDetailsToBatchStatistics } from '@v2/pages/common/components/NCFileBatchEvents/helpers';
import { Alert } from '@helpers';

interface UseOptimisticStatisticsUpdateParams {
  eventsPerspective: EventsPerspective | null;
  ncFileId?: number | null;
  setNcFileStatistics: React.Dispatch<React.SetStateAction<NCFileStatisticsTimesType | null>>;
}

interface UseOptimisticStatisticsUpdateReturn {
  handleOptimisticUpdate: (updatedFields: Partial<ClampingDetails>) => void;
  refetchStatistics: () => Promise<void>;
}

const useOptimisticStatisticsUpdate = ({
  eventsPerspective,
  ncFileId,
  setNcFileStatistics,
}: UseOptimisticStatisticsUpdateParams): UseOptimisticStatisticsUpdateReturn => {
  const dispatch = useAppDispatch();
  const batchData = useSelector(selectSingleBatchData);

  const handleOptimisticUpdate = useCallback(
    (updatedFields: Partial<ClampingDetails>) => {
      const shouldUpdateNcFileStatistics = eventsPerspective === EventsPerspective.NC_FILE || eventsPerspective === EventsPerspective.SINGLE_MACHINE;

      if (shouldUpdateNcFileStatistics) {
        setNcFileStatistics((prev) =>
          prev
            ? {
                ...prev,
                clampingDetails: { ...prev.clampingDetails, ...updatedFields },
              }
            : prev
        );
      }

      if (eventsPerspective === EventsPerspective.BATCH && batchData?.data) {
        const batchUpdates = mapClampingDetailsToBatchStatistics(updatedFields, batchData.data);
        dispatch(setBatch({ ...batchData.data, ...batchUpdates }));
      }
    },
    [eventsPerspective, setNcFileStatistics, batchData, dispatch]
  );

  const refetchStatistics = useCallback(async () => {
    if (ncFileId == null) return;

    try {
      const statistics: NCFileStatisticsTimesType = await dispatch(ncFileStatisticsTimes({ ncFileId }));
      if (statistics) {
        setNcFileStatistics(statistics);
      }
    } catch (e) {
      console.error(e);
      Alert({ title: 'Error during fetching ncFileStatisticsTimes', text: 'Please try again' });
    }
  }, [ncFileId, dispatch, setNcFileStatistics]);

  return { handleOptimisticUpdate, refetchStatistics };
};

export { useOptimisticStatisticsUpdate };
